// Typed client for google-api-service edge function.
// Each method maps to one edge function action. Auth error handling (OAuth redirect)
// is handled by the FE caller, not here.

import { BaseEdgeFnClient } from './BaseEdgeFnClient.ts';
import type { SupabaseClient } from '../base.ts';
import type { TransactionFile } from '@shared/types/transaction.ts';

// ── Response types ──────────────────────────────────────────────────────

export interface PreparedEmail {
  subject: string;
  html_body: string;
  message_html: string;
  footer_html: string;
  plain_text?: string;
  recipients: string[];
  cc: string[];
  attachments: TransactionFile[];
  email_type: string;
  validation_issues?: string[];
  placeholder_values?: Record<string, unknown>;
}

export interface SentEmail {
  success: boolean;
  /** Anchor row — the batch's FIRST transaction, or the only row for a single send. */
  email_id: number;
  /** Batch sends only: every `email_sends` row the fan-out created. */
  email_ids?: number[];
  /** Batch sends only: the uuid grouping those rows. */
  batch_id?: string | null;
  gmail_message_id: string | null;
  gmail_thread_id: string | null;
  message: string;
}

/** Projected attachment weight for a prospective send, against Gmail's ceiling. */
export interface AttachmentSizeProjection {
  total_bytes: number;
  limit_bytes: number;
  over_limit: boolean;
  file_count: number;
}

export interface GeneratedDocument {
  fileId: number;
  driveFileId: string;
  fileName: string;
  pdfDriveFileId?: string;
}

export interface UploadedFile {
  id: number;
  fileName: string;
  fileId: string;
  documentType: string;
  folderDbId: number;
}

export interface AssignExtractionResult {
  success: boolean;
  /** True when a Drive-backed file row existed and was moved/attached. */
  fileMoved: boolean;
  fileId: number | null;
  transactionId: number;
  /** Set when the file move succeeded but the extraction match write failed. */
  partial?: boolean;
  error?: string;
}

/** Result of the login-time token liveness probe (`token.health`). */
export interface GoogleTokenHealth {
  ok: boolean;
  /** True only when the edge fn confirmed re-auth is needed (dead/missing token, missing scopes). */
  requiresReauth?: boolean;
  code?: string;
}

// ── Client ──────────────────────────────────────────────────────────────

export class GoogleEdgeFnClient extends BaseEdgeFnClient {
  constructor(sb: SupabaseClient) {
    super(sb);
  }

  private async google<T>(method: string, body: Record<string, unknown> = {}): Promise<T> {
    return this.callOrThrow<T>('google-api-service', { method, body });
  }

  // ── Gmail watch ─────────────────────────────────────────────────────

  async renewGmailWatch(): Promise<void> {
    await this.callOrThrow<void>('renew-gmail-watch', {});
  }

  // ── Email ───────────────────────────────────────────────────────────
  // Note: email.prepare is now local (EmailService). Only send goes through edge fn.

  async sendEmail(params: {
    transactionId?: number;
    invoiceId?: number;
    /** Batch scope — ONE Gmail message, one `email_sends` row per id sharing a `batch_id`. */
    transactionIds?: number[];
    emailType: string;
    subject: string;
    htmlBody: string;
    recipients: string[];
    cc: string[];
    attachments: number[];
    isDraft: boolean;
  }): Promise<SentEmail> {
    return this.google<SentEmail>('email.send', {
      transaction_id: params.transactionId,
      transaction_ids: params.transactionIds,
      invoice_id: params.invoiceId,
      email_type: params.emailType,
      subject: params.subject,
      html_body: params.htmlBody,
      recipients: params.recipients,
      cc: params.cc,
      attachments: params.attachments,
      is_draft: params.isDraft,
    });
  }

  /**
   * Project total attachment weight WITHOUT downloading anything (Drive metadata
   * only). `transaction_files` carries no size column, so this cannot be answered
   * from Postgres. Used by the batch compose modal to show the projected size and
   * block an oversized send before the attempt.
   */
  async getAttachmentSize(fileIds: number[]): Promise<AttachmentSizeProjection> {
    return this.google<AttachmentSizeProjection>('email.attachmentSize', {
      attachments: fileIds,
    });
  }

  // ── Documents ─────────────────────────────────────────────────────────

  async fillTransactionTemplate(
    templateName: string,
    transactionId: number,
    transportId?: number,
    additionalData?: Record<string, unknown>,
  ): Promise<GeneratedDocument> {
    return this.google<GeneratedDocument>('docs.fillTransactionTemplate', {
      templateName,
      transactionId,
      transportId,
      additionalData,
    });
  }

  // ── Drive ─────────────────────────────────────────────────────────────

  async uploadFiles(
    transactionId: number,
    files: Array<{
      file: File | Blob;
      fileName: string;
      mimeType: string;
      documentType: string;
      documentNumber?: string | null;
      documentStatus?: string;
      receivedDate?: string | null;
      reviewStatus?: string | null;
      splitPerPage?: boolean;
      convertToPdf?: boolean;
      notifyChat?: boolean;
    }>,
  ): Promise<{ files: UploadedFile[] }> {
    const fileEntries: Record<string, File | Blob> = {};
    const notifyChat = files.some(f => f.notifyChat);
    const metadata = files.map((f, i) => {
      fileEntries[`file_${i}`] = f.file;
      return {
        fileName: f.fileName,
        mimeType: f.mimeType,
        documentType: f.documentType,
        documentNumber: f.documentNumber,
        documentStatus: f.documentStatus,
        receivedDate: f.receivedDate,
        reviewStatus: f.reviewStatus,
        splitPerPage: f.splitPerPage,
        convertToPdf: f.convertToPdf,
      };
    });

    return this.callWithFiles<{ files: UploadedFile[] }>(
      'google-api-service',
      { method: 'drive.uploadMultipleFilesMultipart', body: { transactionId, files: metadata, notifyChat } },
      fileEntries,
    );
  }

  async moveFileToTransaction(fileId: number, transactionId: number): Promise<void> {
    // Handler destructures `targetTransactionId` — sending `transactionId` left it
    // undefined and the move threw "Transaction undefined not found". Contract fix.
    await this.google('drive.moveFileToTransaction', { fileId, targetTransactionId: transactionId });
  }

  async acceptAndMoveFiles(fileIds: number[], transactionId: number): Promise<void> {
    await this.google('drive.acceptAndMoveFiles', { fileIds, transactionId });
  }

  /**
   * Atomic manual assign: records the extraction match AND physically files the
   * document (Drive move + transaction_files.transaction_id) in ONE server-side
   * action. Returns `{ success, fileMoved }` on success, or a partial-failure
   * payload (`success:false, partial:true`) when the move succeeded but the
   * extraction match write failed — the caller must surface that, not swallow it.
   */
  async assignExtraction(
    extractionId: string,
    transactionId: number,
  ): Promise<AssignExtractionResult> {
    return this.google<AssignExtractionResult>('drive.assignExtraction', {
      extractionId,
      transactionId,
    });
  }

  async uploadCompanyDocument(
    companyId: number | null,
    file: File,
  ): Promise<{ driveFileId: string; mimeType: string; fileName: string }> {
    return this.callWithFiles<{ driveFileId: string; mimeType: string; fileName: string }>(
      'google-api-service',
      {
        method: 'drive.uploadCompanyDocument',
        body: { companyId, fileName: file.name, mimeType: file.type || 'application/octet-stream' },
      },
      { file },
    );
  }

  async uploadToFolder(
    parentFolderId: string,
    file: File | Blob,
    fileName: string,
    mimeType: string,
  ): Promise<{ fileId: string; fileName: string; mimeType: string }> {
    return this.callWithFiles<{ fileId: string; fileName: string; mimeType: string }>(
      'google-api-service',
      {
        method: 'drive.uploadToFolder',
        body: { parentFolderId, fileName, mimeType },
      },
      { file },
    );
  }

  // ── Token ─────────────────────────────────────────────────────────────

  /**
   * Login-time liveness probe for the stored Google token. Never throws:
   * transport or unexpected edge-fn failures come back as
   * `{ ok: false, requiresReauth: false }` so callers only act on a
   * CONFIRMED dead token, never on a network blip.
   */
  async tokenHealth(scopes: string[]): Promise<GoogleTokenHealth> {
    try {
      const res = await this.call<GoogleTokenHealth>('google-api-service', {
        method: 'token.health',
        body: { scopes },
      });
      if (!res.success) return { ok: false, requiresReauth: false };
      return res.data;
    } catch {
      return { ok: false, requiresReauth: false };
    }
  }
}
